import { FC, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import { BlobProvider } from '@react-pdf/renderer';
import {
  ArrowsExpandIcon,
  DownloadIcon,
  DocumentTextIcon,
} from '@heroicons/react/outline';
import {
  Line,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

import {
  AssessmentSchemaType,
  AssessmentType,
  CompletedProgramAssessmentsDataQuery,
  useCompletedProgramAssessmentsDataLazyQuery,
  useCreateProgramNoteMutation,
  usePatientCompletedProgramAssessmentsDataLazyQuery,
} from '../../../../../generated/graphql';

import {
  AnalyticsPage,
  AnalyticsComponent,
  trackProviderComponentViewedEvent,
} from '../../../../../lib/analytics';

import { getTimeStamp } from '../../../../lib/time';
import { defaultTransitionProps } from '../../../../lib/animation';

import { colors } from '../../../../lib/colors';
import { downloadAllPdfs, handlePdfDownload } from '../../../../lib/pdf';
import { AssessmentHistorySelection } from '../../../../lib/assessments';

import Slideover, {
  SLIDEOVER_TRANSITION_DURATION,
} from '../../../../components/Slideover';
import SlideoverSection from '../../../../components/SlideoverSection';
import Button from '../../../../components/Button';
import { customToast } from '../../../../components/ToastAlert/customToast';

import InterpretationSection from '../../../../components/Assessments/InterpretationSection';
import ScoreSection from '../../../../components/Assessments/ScoreSection';
import ResponsesSection from '../../../../components/Assessments/ResponsesSection';
import HistorySection from '../../../../components/Assessments/HistorySection';
import FormResponsesModal from '../../../../components/Modals/FormResponsesModal';
import {
  CompletedProgramAssessment,
  getCompletedAssessmentGraphData,
} from '../../../../components/Assessments/helpers';
import ProgramAssessmentPDF from '../../../../components/ProgramActivitySlideover/ProgramAssessmentPDF';
import { createNoteFromForm } from '../../../../lib/createNoteFromForm';
import NoteCreatedConfirmationModal from '../../../../components/NoteCreatedConfirmationModal';
import VerticalSignatureDisplay from '../../../../components/ProgramActivityCard/VerticalSignatureDisplay';

import {
  CustomAxisTick,
  CustomizedDot,
  CustomTooltip,
  GraphLegend,
} from './CustomGraph';

const AssessmentsCompletedSlideover: FC<{
  onClose: () => void;
  isOpen: boolean;
  patientFirstName?: string;
  programId?: string;
  selectedAssessment?: AssessmentHistorySelection | null;
  patientMode?: boolean;
  hideInterpretation?: boolean;
}> = ({
  onClose,
  isOpen,
  patientFirstName,
  programId,
  selectedAssessment,
  patientMode = false,
  hideInterpretation,
}) => {
  const shouldHideInterpretation = hideInterpretation ?? patientMode;
  const [shownAssessmentQandAIndex, setShownAssessmentQandAIndex] = useState<
    number | null
  >(null);
  const [isFormResponsesModalOpen, setIsFormResponsesModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [createdNoteTitle, setCreatedNoteTitle] = useState('');
  const [createProgramNote] = useCreateProgramNoteMutation();

  const assessmentQandAShown = Boolean(shownAssessmentQandAIndex !== null);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setShownAssessmentQandAIndex(null);
      }, SLIDEOVER_TRANSITION_DURATION);
    }
  }, [isOpen]);

  const [
    getCompletedProgramAssessmentsData,
    {
      data: completedProgramAssessmentsData,
      error: completedProgramAssessmentsError,
      loading: isLoadingCompletedProgramAssessments,
    },
  ] = useCompletedProgramAssessmentsDataLazyQuery();

  const [
    getPatientCompletedProgramAssessmentsData,
    {
      data: patientCompletedProgramAssessmentsData,
      error: patientCompletedProgramAssessmentsError,
      loading: isLoadingPatientCompletedProgramAssessments,
    },
  ] = usePatientCompletedProgramAssessmentsDataLazyQuery();

  useEffect(() => {
    if (!selectedAssessment) {
      return;
    }

    if (patientMode) {
      getPatientCompletedProgramAssessmentsData({
        variables: {
          assessmentId: selectedAssessment.id,
        },
      });
      return;
    }

    if (programId) {
      getCompletedProgramAssessmentsData({
        variables: {
          programId,
          assessmentId: selectedAssessment.id,
        },
      });
      trackProviderComponentViewedEvent(
        AnalyticsComponent.AssessmentSlideover,
        AnalyticsPage.ClientProfile,
        {
          assessmentType: selectedAssessment.type,
        },
      );
    }
  }, [selectedAssessment, patientMode, programId]);

  const queryError = patientMode
    ? patientCompletedProgramAssessmentsError
    : completedProgramAssessmentsError;
  const isLoading = patientMode
    ? isLoadingPatientCompletedProgramAssessments
    : isLoadingCompletedProgramAssessments;

  useEffect(() => {
    if (queryError) {
      customToast.error('Unable to fetch completed assessments.');
    }
  }, [queryError]);

  const completedProgramAssessments = (
    patientMode
      ? patientCompletedProgramAssessmentsData?.patientCompletedProgramAssessmentsByAssessment
      : completedProgramAssessmentsData?.completedProgramAssessmentsByAssessment
  ) as
    | CompletedProgramAssessmentsDataQuery['completedProgramAssessmentsByAssessment']
    | undefined;

  const instrument = patientMode
    ? completedProgramAssessments?.[0]?.assessment
    : completedProgramAssessmentsData?.assessment;

  const assessmentType =
    selectedAssessment?.type ?? instrument?.type ?? undefined;
  const schemaType =
    selectedAssessment?.schemaType ?? instrument?.schemaType ?? undefined;

  const assessmentTimestampTitle =
    shownAssessmentQandAIndex !== null
      ? getTimeStamp(
          completedProgramAssessments?.[shownAssessmentQandAIndex]?.completedAt,
          false,
        )
      : '';

  const { graphData, hasSecondaryScore, hasTertiaryScore, graphTitle } =
    getCompletedAssessmentGraphData(
      completedProgramAssessments as CompletedProgramAssessment[] | undefined,
    );

  const isCustomAssessment = schemaType === AssessmentSchemaType.Custom;
  const isNonScoredAssessment = schemaType === AssessmentSchemaType.NonScored;
  const hideScoring = isCustomAssessment || isNonScoredAssessment;
  const showGraph = !hideScoring && (graphData?.length ?? 0) >= 2;

  const title = isCustomAssessment
    ? selectedAssessment?.name ?? instrument?.name ?? ''
    : selectedAssessment?.shortName ?? instrument?.shortName ?? '';

  const subtitle = assessmentQandAShown
    ? assessmentTimestampTitle
    : !isCustomAssessment
    ? `${patientFirstName}'s ${
        selectedAssessment?.name ?? instrument?.name ?? ''
      }`
    : undefined;

  const handleCreateNote = async () => {
    if (
      !completedProgramAssessments ||
      completedProgramAssessments.length === 0 ||
      !programId
    ) {
      customToast.error(
        'No completed assessments available to create note from',
      );
      return;
    }

    const selectedCompletion =
      shownAssessmentQandAIndex !== null
        ? completedProgramAssessments[shownAssessmentQandAIndex]
        : completedProgramAssessments[0];

    await createNoteFromForm({
      programId,
      programAssessment: selectedCompletion,
      patientFirstName,
      createProgramNote,
      onSuccess: (noteTitle) => {
        setCreatedNoteTitle(noteTitle);
        setIsConfirmationModalOpen(true);
      },
    });
  };

  const shownCompletion =
    shownAssessmentQandAIndex !== null
      ? completedProgramAssessments?.[shownAssessmentQandAIndex]
      : undefined;
  const hasSignatures = Boolean(
    shownCompletion?.programAssessmentSignatureUsers?.length,
  );

  return (
    <Slideover
      isOpen={isOpen}
      fetching={isLoading}
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      backButtonActive={assessmentQandAShown}
      onBackButtonClick={() => setShownAssessmentQandAIndex(null)}
      showContent={
        !isLoading &&
        !queryError &&
        Boolean(
          patientMode
            ? patientCompletedProgramAssessmentsData
            : completedProgramAssessmentsData,
        )
      }
    >
      <Transition
        show={shownAssessmentQandAIndex === null}
        {...defaultTransitionProps}
      >
        {showGraph && (
          <SlideoverSection
            title={graphTitle}
            className="pl-0"
            titleClassName="px-6"
          >
            {hasTertiaryScore && (
              <GraphLegend
                items={[
                  { label: 'Personal', style: 'solid' },
                  { label: 'Work', style: 'dashed' },
                  { label: 'Client', style: 'dotted' },
                ]}
              />
            )}
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={graphData}
                  margin={{ top: 5, left: -8, right: 15, bottom: 5 }}
                  width={3}
                  height={4}
                >
                  <CartesianGrid stroke={colors.neutral[50]} vertical={false} />
                  <XAxis
                    dataKey="timestamp"
                    tickLine={false}
                    axisLine={false}
                    tick={<CustomAxisTick isXAxis />}
                    dy={10}
                  />
                  <YAxis
                    dx={-20}
                    axisLine={false}
                    tickLine={false}
                    tick={<CustomAxisTick isYAxis />}
                    domain={[
                      instrument?.scoreRange?.min ?? 0,
                      instrument?.scoreRange?.max ?? 100,
                    ]}
                  />
                  <Line
                    type="linear"
                    dataKey="primaryScore"
                    stroke={colors.neutral[100]}
                    dot={<CustomizedDot />}
                    activeDot={
                      <CustomizedDot
                        isActiveDot
                        setShownAssessmentQandAIndex={
                          setShownAssessmentQandAIndex
                        }
                      />
                    }
                    isAnimationActive={false}
                  />
                  {hasSecondaryScore && (
                    <Line
                      type="linear"
                      dataKey="secondaryScore"
                      stroke={colors.neutral[100]}
                      strokeDasharray="4 4"
                      dot={<CustomizedDot isSecondaryScore />}
                      activeDot={
                        <CustomizedDot
                          isActiveDot
                          setShownAssessmentQandAIndex={
                            setShownAssessmentQandAIndex
                          }
                          isSecondaryScore
                        />
                      }
                      isAnimationActive={false}
                    />
                  )}
                  {hasTertiaryScore && (
                    <Line
                      type="linear"
                      dataKey="tertiaryScore"
                      stroke={colors.neutral[100]}
                      strokeDasharray="2 3"
                      dot={<CustomizedDot isTertiaryScore />}
                      activeDot={
                        <CustomizedDot
                          isActiveDot
                          setShownAssessmentQandAIndex={
                            setShownAssessmentQandAIndex
                          }
                          isTertiaryScore
                        />
                      }
                      isAnimationActive={false}
                    />
                  )}
                  <Tooltip cursor={false} content={<CustomTooltip />} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </SlideoverSection>
        )}
        {!hideScoring && !shouldHideInterpretation && (
          <InterpretationSection assessment={instrument} />
        )}
        <HistorySection
          completedProgramAssessments={completedProgramAssessments}
          assessmentType={assessmentType as AssessmentType | undefined}
          assessmentSchemaType={schemaType}
          setShownAssessmentQandAIndex={setShownAssessmentQandAIndex}
        />
        <SlideoverSection>
          <Button
            title="Download PDFs"
            onClick={() => {
              downloadAllPdfs(
                completedProgramAssessments,
                patientFirstName,
                !patientMode,
              )
                .then((count) => {
                  customToast.success(
                    `Downloaded ${count} PDF${count !== 1 ? 's' : ''}`,
                  );
                })
                .catch((error) => {
                  console.error(`Failed to generate PDFs: ${error.message}`);
                  customToast.error(
                    `Failed to generate PDFs: ${error.message}`,
                  );
                });
            }}
            className="w-full"
            size="small"
            theme="primary"
            IconComponent={DownloadIcon}
          />
        </SlideoverSection>
        {!patientMode && (
          <SlideoverSection
            title="About"
            textContent={instrument?.description}
          />
        )}
      </Transition>
      <Transition show={assessmentQandAShown} {...defaultTransitionProps}>
        {shownAssessmentQandAIndex !== null && shownCompletion && (
          <>
            <SlideoverSection>
              <div className="flex w-full flex-col gap-3">
                <BlobProvider
                  document={
                    <ProgramAssessmentPDF
                      programAssessment={shownCompletion}
                      isProviderUser={!patientMode}
                    />
                  }
                >
                  {({ blob, loading, error }) => (
                    <Button
                      title={loading ? 'Generating PDF...' : 'Download PDF'}
                      onClick={() =>
                        handlePdfDownload(
                          blob,
                          shownCompletion,
                          patientFirstName,
                          () =>
                            customToast.success('PDF downloaded successfully'),
                        )
                      }
                      className="w-full"
                      disabled={loading || Boolean(error)}
                      size="small"
                      theme="primary"
                      IconComponent={DownloadIcon}
                    />
                  )}
                </BlobProvider>
                {!patientMode && (
                  <>
                    <Button
                      title="Expand Responses"
                      onClick={() => setIsFormResponsesModalOpen(true)}
                      className="w-full"
                      theme="primary"
                      size="small"
                      IconComponent={ArrowsExpandIcon}
                    />
                    <Button
                      title="Create Private Note"
                      onClick={handleCreateNote}
                      className="w-full"
                      size="small"
                      theme="primary"
                      IconComponent={DocumentTextIcon}
                      disabled={!shownCompletion.completedAt}
                    />
                  </>
                )}
              </div>
            </SlideoverSection>
            {!hideScoring && (
              <ScoreSection
                assessmentType={assessmentType as AssessmentType | undefined}
                title="Score"
                completedAssessment={shownCompletion}
              />
            )}
            {Boolean(shownCompletion.additionalThoughts) && (
              <SlideoverSection
                title={`Notes from ${patientFirstName}`}
                textContent={
                  `"${shownCompletion.additionalThoughts}"` ?? undefined
                }
              />
            )}
            <ResponsesSection
              title="Responses"
              questionFrame={instrument?.questionFrame ?? undefined}
              programAssessment={shownCompletion}
            />
            {hasSignatures && (
              <SlideoverSection title="Signatures">
                <VerticalSignatureDisplay
                  programActivity={
                    {
                      activityType: 'Assessment' as never,
                      programAssessment: shownCompletion,
                    } as never
                  }
                  className="mt-2"
                  forceFullDisplay
                />
              </SlideoverSection>
            )}
            {!patientMode && (
              <SlideoverSection
                title="About"
                textContent={instrument?.description}
              />
            )}
          </>
        )}
      </Transition>

      <FormResponsesModal
        isOpen={isFormResponsesModalOpen}
        onClose={() => setIsFormResponsesModalOpen(false)}
        programAssessment={shownCompletion}
        assessmentName={selectedAssessment?.name ?? instrument?.name}
        patientName={patientFirstName}
      />

      <NoteCreatedConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        noteTitle={createdNoteTitle}
        clientFirstName={patientFirstName}
      />
    </Slideover>
  );
};

export default AssessmentsCompletedSlideover;
